/**
 * Darstellungshelfer der Oberfläche.
 *
 * Hier steht bewusst nur Formatierung – keine Fachlogik. Fälligkeiten, Alter
 * und Zählweisen kommen fertig berechnet vom Server.
 */

const WOCHENTAGE = ['So', 'Mo', 'Di', 'Mi', 'Do', 'Fr', 'Sa'];

/** ISO-Datum als TT.MM.JJ. */
export function dat(iso: string | null | undefined): string {
  if (!iso) return '—';
  const d = new Date(iso + 'T00:00:00');
  if (Number.isNaN(d.getTime())) return '—';
  return (
    String(d.getDate()).padStart(2, '0') +
    '.' +
    String(d.getMonth() + 1).padStart(2, '0') +
    '.' +
    String(d.getFullYear()).slice(2)
  );
}

export function wochentag(iso: string): string {
  return WOCHENTAGE[new Date(iso + 'T00:00:00').getDay()] ?? '';
}

/**
 * Zahl mit Tausenderpunkten: 31500 → "31.500".
 *
 * Bewusst von Hand gruppiert statt über toLocaleString('de-AT'): die aktuellen
 * Browser liefern für de-AT ein schmales geschütztes Leerzeichen als Trenner
 * ("31 500"). Das Design verlangt den Punkt, und der Trenner muss in Oberfläche,
 * Server und Mailtext identisch aussehen.
 */
export function gruppiere(n: number): string {
  const gerundet = Math.round(Math.abs(n));
  const ziffern = String(gerundet).replace(/\B(?=(\d{3})+(?!\d))/g, '.');
  return (n < 0 ? '-' : '') + ziffern;
}

/** Betrag als "24.500 €". */
export function eur(n: number | null | undefined): string {
  if (n === null || n === undefined || Number.isNaN(n)) return '—';
  /*
   * Typografisches Minus, kein Bindestrich: Der Bindestrich ist schmaler,
   * sitzt tiefer und liest sich in einer Zahlenspalte wie ein Trennstrich.
   */
  return gruppiere(n).replace('-', '−') + ' €';
}

/** Eingabe eines Betrags: nur Ziffern, live mit Tausenderpunkten. */
export function betragFormatieren(eingabe: string): string {
  const roh = String(eingabe).replace(/[^0-9]/g, '');
  if (roh === '') return '';
  return gruppiere(parseInt(roh, 10));
}

export function betragLesen(eingabe: string): number | null {
  const roh = String(eingabe).replace(/[^0-9]/g, '');
  return roh === '' ? null : parseInt(roh, 10);
}

/** Ganze Tage zwischen einem ISO-Datum und dem Bezugstag vom Server. */
export function tageSeit(iso: string, heute: string): number {
  return Math.round(
    (new Date(heute + 'T00:00:00').getTime() - new Date(iso + 'T00:00:00').getTime()) / 86_400_000,
  );
}

/** Aktuelle Uhrzeit als "HH:MM". */
export function jetztUhrzeit(): string {
  const d = new Date();
  return String(d.getHours()).padStart(2, '0') + ':' + String(d.getMinutes()).padStart(2, '0');
}

export function initialen(name: string): string {
  const roh = (name || '')
    .replace(/^(Ing\.|Dr\.|DI|Mag\.|Familie|Zimmerei|Bauträger)\s+/i, '')
    .trim();
  const teile = roh.split(/[\s-]+/).filter(Boolean);
  return ((teile[0]?.[0] ?? '?') + (teile[1]?.[0] ?? '')).toUpperCase();
}

/**
 * Initialen eines Team-Mitglieds – aus dem vollen Namen, alle Bestandteile.
 * Michal Bela → MB, Martin Zihr → MZ, Tibor Poos-Kallinger → TPK.
 * Zwei Buchstaben genügen im Team nicht: Michal und Martin ergäben beide „M".
 * Muss deckungsgleich mit `teamInitialen` im Server bleiben.
 */
export function teamInitialen(anzeigename: string): string {
  const roh = (anzeigename || '')
    .replace(/^(Ing\.|Dr\.|DI|Mag\.|Familie|Zimmerei|Bauträger)\s+/i, '')
    .trim();
  const teile = roh.split(/[\s-]+/).filter(Boolean);
  if (teile.length === 0) return '?';
  return teile
    .map((t) => t[0] ?? '')
    .join('')
    .toUpperCase();
}

export function norm(s: string | null | undefined): string {
  return (s || '').toLowerCase().replace(/[^a-z0-9äöüß@.]/g, '');
}

/** "1 Vorgang" / "3 Vorgänge". */
export function anzahlText(n: number, einzahl: string, mehrzahl: string): string {
  return `${n} ${n === 1 ? einzahl : mehrzahl}`;
}

/** Wörter, die eine Organisation statt einer Privatperson anzeigen. */
const FIRMEN_WORT =
  /\b(gmbh|ges\.?m\.?b\.?h|kg|og|ag|e\.?u\.?|immo|praxis|arztpraxis|ordination|hausverwaltung|bau|fenster|holz|verein|gemeinde|hotel|apotheke)\b/i;

/** Titel, die vor dem Namen stehen und nicht Teil des Namens sind. */
const TITEL = /^(Ing\.|Dr\.|DI|Mag\.)\s+/i;

export interface NameZerlegt {
  typ: 'person' | 'organisation';
  anrede: string;
  titel: string;
  vorname: string;
  nachname: string;
  firma: string;
  /**
   * Der Ort, wenn im Text einer steckte – aus der Klammer oder hinter dem
   * Unterstrich. Er gehört in die Ortsspalte, nicht in den Namen.
   */
  ort: string;
}

/**
 * Zerlegt einen eingetippten Namen in die Felder der Kundenmaske.
 *
 * Wird gebraucht, wenn aus der Suche heraus ein neuer Kunde angelegt wird: Was
 * schon getippt wurde, soll nicht noch einmal getippt werden müssen.
 *
 * Die Regel ist bewusst dieselbe wie in `kundeAus` am Server (abzug.ts) – wer
 * sie hier ändert, muss sie dort mitändern. Zwei Namen, zwei Ergebnisse wäre
 * schlimmer als eine Regel, die gelegentlich danebenliegt: Die Felder stehen
 * im Formular und lassen sich in zwei Sekunden richtigstellen.
 */
export function nameZerlegen(text: string): NameZerlegt {
  const leer: NameZerlegt = {
    typ: 'person',
    anrede: '',
    titel: '',
    vorname: '',
    nachname: '',
    firma: '',
    ort: '',
  };

  /*
   * Angebotskürzel aufteilen.
   *
   * „Bachofner_Velm Neuhofsee" ist kein Kundenname, sondern der Name eines
   * Vorgangsordners: Nachname, Unterstrich, Ort. Wer das ungeteilt speichert,
   * hat den Ort für immer im Namensfeld – so sind die Altbestände entstanden.
   * Eine vorangestellte Nummer und ein Gewerkkürzel gehören ebenso wenig zum
   * Namen. Was hier herauskommt, ist ein Vorschlag; bestätigt wird er im
   * Formular, wo Name und Ort sichtbar nebeneinander stehen.
   */
  const roh = (text || '').trim();
  let ort = '';
  let quelle = roh;

  if (roh.includes('_')) {
    const teile = roh
      .split('_')
      .map((t) => t.trim())
      .filter(Boolean)
      .filter((t) => !/^[0-9]{5}[a-z]?$/i.test(t) && !/^[A-ZÄÖÜ]{2,3}$/.test(t));
    quelle = teile[0] ?? '';
    if (teile.length > 1) ort = teile[teile.length - 1] ?? '';
  }

  // Der Klammerzusatz ist ebenfalls der Ort – bisher fiel er ersatzlos weg.
  const klammer = /\(([^)]*)\)\s*$/.exec(quelle);
  if (klammer && !ort) ort = (klammer[1] ?? '').trim();

  const ohneKlammer = quelle.replace(/\([^)]*\)\s*$/, '').trim();
  let kern = (ohneKlammer.split(',')[0] ?? '').trim().replace(/\s+/g, ' ');
  if (!kern) return { ...leer, ort };

  let anrede = '';
  if (/^Familie\s+/i.test(kern)) {
    anrede = 'Familie';
    kern = kern.replace(/^Familie\s+/i, '').trim();
  }

  let titel = '';
  const titelTreffer = TITEL.exec(kern);
  if (titelTreffer) {
    titel = (titelTreffer[1] ?? '').trim();
    kern = kern.replace(TITEL, '').trim();
  }

  if (!kern) return { ...leer, anrede, titel, ort };

  // Ziffern oder ein Firmenwort: das ist keine Privatperson.
  if (/\d/.test(kern) || FIRMEN_WORT.test(kern)) {
    return { ...leer, typ: 'organisation', firma: kern, ort };
  }

  const woerter = kern.split(' ').filter(Boolean);
  if (woerter.length === 1) {
    return { ...leer, anrede, titel, nachname: woerter[0]!, ort };
  }

  // Bei mehreren Wörtern gilt das letzte als Nachname – „Martin Zihr" ebenso
  // wie „Anna Maria Huber". Umgekehrte Schreibweisen kommen vor, sind aber
  // seltener; korrigiert wird im Formular.
  return {
    ...leer,
    anrede,
    titel,
    ort,
    vorname: woerter.slice(0, -1).join(' '),
    nachname: woerter[woerter.length - 1]!,
  };
}

/**
 * Sieht das nach einer E-Mail-Adresse aus?
 *
 * Bewusst ohne regulären Ausdruck: Die Escape-Zeichen darin sind eine
 * Fehlerquelle, und eine vollständige Prüfung nach RFC ist ohnehin nicht
 * möglich. Geprüft wird das, was in der Praxis schiefgeht – vergessenes @,
 * fehlende Domainendung, Leerzeichen aus der Zwischenablage.
 *
 * Ob die Adresse wirklich existiert, weiß erst der Mailserver.
 */
export function mailPlausibel(wert: string): boolean {
  const text = wert.trim();
  if (!text || text.includes(' ')) return false;

  const teile = text.split('@');
  if (teile.length !== 2) return false;

  const [vorn, hinten] = teile as [string, string];
  if (!vorn) return false;

  const punkt = hinten.lastIndexOf('.');
  // Domain braucht einen Punkt, davor und dahinter mindestens ein Zeichen.
  return punkt > 0 && punkt < hinten.length - 1;
}

/**
 * Wandelt eine Telefonnummer in die Form, die WhatsApp erwartet: nur Ziffern,
 * mit Landesvorwahl, ohne Plus.
 *
 * Bewusst ohne regulaeren Ausdruck geschrieben - die Escape-Sequenzen sind bei
 * uns beim Schreiben der Datei schon mehrfach verlorengegangen, und eine
 * stumm falsche Nummer faellt niemandem auf.
 *
 * "+43 660 884 21 09" und "0660 884 21 09" ergeben beide "436608842109".
 * Oesterreich ist die Annahme fuer Nummern ohne Vorwahl - alle anderen
 * schreibt man bei uns ohnehin international.
 */
export function whatsappNummer(roh: string): string {
  let ziffern = '';
  for (const z of String(roh || '')) {
    if (z >= '0' && z <= '9') ziffern += z;
  }
  if (!ziffern) return '';

  // Fuehrende Doppelnull ist die geschriebene Form des Plus.
  if (ziffern.startsWith('00')) return ziffern.slice(2);
  // Eine einzelne Null ist die nationale Form.
  if (ziffern.startsWith('0')) return '43' + ziffern.slice(1);
  return ziffern;
}

/**
 * Ist das plausibel eine Mobilnummer?
 *
 * WhatsApp auf einem Festnetzanschluss fuehrt ins Leere, deshalb bekommt nur
 * eine Mobilnummer den WhatsApp-Link. Oesterreichische Mobilnummern beginnen
 * national mit 06, international mit 436.
 */
export function istMobil(roh: string): boolean {
  const n = whatsappNummer(roh);
  return n.startsWith('436') || n.startsWith('06');
}

/**
 * Kurzform für Achsen und enge Spalten: 42800 → „42,8k", −15900 → „−15,9k".
 *
 * Dieselbe Regel wie am Server, damit dieselbe Zahl in Diagramm und Mail
 * gleich aussieht. Ab 100.000 entfällt die Nachkommastelle – dort ist sie
 * kein Gewinn an Genauigkeit, nur an Breite.
 */
export function eurKurz(n: number): string {
  /* Die Nulllinie heißt „0", nicht „0,0k". */
  if (n === 0) return '0';
  const zeichen = n < 0 ? '−' : '';
  const betrag = Math.abs(n);
  /* Ab einer Million auf Mio umstellen: „1000k" liest niemand als Million. */
  if (betrag >= 1_000_000) {
    return zeichen + (betrag / 1_000_000).toFixed(1).replace('.', ',') + ' Mio';
  }
  return zeichen + (betrag / 1000).toFixed(betrag < 100_000 ? 1 : 0).replace('.', ',') + 'k';
}